import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import OraTimeButton from "../components/voice/OraTimeButton";
import OrientationControlButton from "../components/OrientationControlButton";

/**
 * TIME Watch → Orologio
 * Rappresentazione minimale e pura del Tempo.
 * - Orologio digitale in tempo reale (HH:MM:SS)
 * - Data completa in italiano (Giorno, D MMMM YYYY)
 * - Aggiornamento ogni secondo, nessuna altra funzionalità.
 */

const pad2 = (n) => String(n).padStart(2, "0");

const WEEKDAYS = [
  "Domenica",
  "Lunedì",
  "Martedì",
  "Mercoledì",
  "Giovedì",
  "Venerdì",
  "Sabato",
];

const MONTHS = [
  "Gennaio",
  "Febbraio",
  "Marzo",
  "Aprile",
  "Maggio",
  "Giugno",
  "Luglio",
  "Agosto",
  "Settembre",
  "Ottobre",
  "Novembre",
  "Dicembre",
];

function formatDateItalian(d) {
  const weekday = WEEKDAYS[d.getDay()];
  const day = d.getDate();
  const month = MONTHS[d.getMonth()];
  const year = d.getFullYear();
  return `${weekday}, ${day} ${month} ${year}`;
}

export default function TimeWatchOrologioPage() {
  const [now, setNow] = useState(() => new Date());
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Fade-in soft dopo il mount
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    // Allinea il primo tick all'inizio del secondo successivo, poi tick ogni 1s.
    let intervalId = null;
    const msToNextSecond = 1000 - (Date.now() % 1000);
    const timeoutId = setTimeout(() => {
      setNow(new Date());
      intervalId = setInterval(() => setNow(new Date()), 1000);
    }, msToNextSecond);

    return () => {
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  const h = pad2(now.getHours());
  const m = pad2(now.getMinutes());
  const s = pad2(now.getSeconds());
  const dateLabel = formatDateItalian(now);

  // Tempo rimanente fino alla mezzanotte del giorno corrente.
  const endOfDay = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    23,
    59,
    59,
    999
  );
  const remainingMs = Math.max(0, endOfDay.getTime() - now.getTime());
  const totalRemSec = Math.floor(remainingMs / 1000) + 1; // include il secondo corrente
  const remH = Math.floor(totalRemSec / 3600);
  const remM = Math.floor((totalRemSec % 3600) / 60);
  const remS = totalRemSec % 60;

  return (
    <div className="px-5 sm:px-8 lg:px-12 py-16 sm:py-20 max-w-4xl mx-auto">
      <Link
        to="/innovazione/time-watch"
        data-testid="time-watch-orologio-back-link"
        className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-white/40 hover:text-white/80 transition mb-10"
      >
        <ChevronLeft className="w-3.5 h-3.5" />
        TIME Watch
      </Link>

      <div
        data-testid="time-watch-orologio-body"
        className={`min-h-[60vh] flex flex-col items-center justify-center text-center transition-opacity duration-[1200ms] ease-out ${
          mounted ? "opacity-100" : "opacity-0"
        }`}
      >
        <div
          data-testid="time-watch-orologio-time"
          className="font-extralight text-white tracking-tight tabular-nums leading-none text-6xl sm:text-8xl lg:text-9xl"
          aria-label="Ora corrente"
        >
          {h}
          <span className="text-white/30 mx-1 sm:mx-2">:</span>
          {m}
          <span className="text-white/30 mx-1 sm:mx-2">:</span>
          {s}
        </div>

        <div
          data-testid="time-watch-orologio-date"
          className="mt-8 sm:mt-10 text-sm sm:text-base font-light text-white/60 tracking-wide"
        >
          {dateLabel}
        </div>

        <div
          data-testid="time-watch-orologio-remaining"
          className="mt-10 sm:mt-12 font-extralight text-emerald-400 tracking-tight tabular-nums leading-none text-2xl sm:text-3xl lg:text-4xl"
          aria-label="Tempo rimanente della giornata"
        >
          {remH}
          <span className="text-emerald-400/60">h </span>
          {pad2(remM)}
          <span className="text-emerald-400/60">m </span>
          {pad2(remS)}
          <span className="text-emerald-400/60">s</span>
        </div>
      </div>
      <OraTimeButton />
      <OrientationControlButton />
    </div>
  );
}
